import { useState } from "react";
import { cn } from "@/lib/utils";

interface Props {
  avatarSrc: string;
  name?: string;
  className?: string;
}

// Uzbek digraphs represent one sound (one "letter"). Treat them as a single
// initial unit so "Shuhrat" → "Sh" (not "S") and "G'ulom" → "G'" (not "G").
const UZ_DIGRAPHS = ["ng", "sh", "ch", "o'", "o‘", "g'", "g‘"];

function takeInitial(word: string): string {
  const lower = word.toLowerCase();
  const digraph = UZ_DIGRAPHS.find((d) => lower.startsWith(d));
  if (digraph) {
    // Preserve original casing: first char upper, rest lower
    return word.charAt(0).toUpperCase() + word.slice(1, digraph.length);
  }
  return word.charAt(0).toUpperCase();
}

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return takeInitial(parts[0]);
  return takeInitial(parts[0]) + takeInitial(parts[parts.length - 1]);
}

const AvatarFrame = (props: Props) => {
  const { avatarSrc, name, className } = props;
  const [failed, setFailed] = useState(false);
  const showFallback = !avatarSrc || failed;

  return (
    <div className={cn("relative flex justify-center items-center", className)}>
      {showFallback ? (
        <div className="size-[95px] pointer-events-none rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex-center bg-gradient-to-br from-orange-100/30 to-orange-100/10 border border-orange-100/20">
          <span className="text-2xl font-bold text-orange-100 leading-none">
            {getInitials(name)}
          </span>
        </div>
      ) : (
        <img
          className="size-[95px] pointer-events-none object-cover rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
          src={avatarSrc}
          alt={name || "avatar"}
          onError={() => setFailed(true)}
        />
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        width="149"
        height="149"
        viewBox="0 0 149 149"
        fill="none"
        className="relative"
      >
        <g filter="url(#filter0_f_18211_99469)">
          <circle
            cx="74.5"
            cy="74.5"
            r="51.5"
            fill="#F7BC61"
            fill-opacity="0.8"
          />
        </g>
        <g clip-path="url(#clip0_18211_99469)">
          <rect
            x="23.5"
            y="27"
            width="97"
            height="97"
            fill="url(#pattern0_18211_99469)"
          />
        </g>
        <rect
          x="25 "
          y="26"
          width="98"
          height="97"
          rx="48.5"
          stroke="#FBC76C"
          stroke-width="2"
        />
        <path
          className="pulsing-star star-1"
          style={{ transformOrigin: "49.5 7.5" }}
          d="M45.5062 6.87009C46.3362 6.70506 47.0987 6.29826 47.6972 5.70108C48.2956 5.10389 48.7033 4.34312 48.8687 3.51486C48.8983 3.36955 48.9773 3.23892 49.0923 3.14509C49.2074 3.05126 49.3514 3 49.5 3C49.6486 3 49.7926 3.05126 49.9077 3.14509C50.0227 3.23892 50.1017 3.36955 50.1313 3.51486C50.2967 4.34312 50.7044 5.10389 51.3028 5.70108C51.9013 6.29826 52.6638 6.70506 53.4938 6.87009C53.6374 6.90199 53.7658 6.98178 53.8578 7.0963C53.9499 7.21082 54 7.35322 54 7.5C54 7.64678 53.9499 7.78918 53.8578 7.9037C53.7658 8.01821 53.6374 8.09801 53.4938 8.12991C52.6638 8.29494 51.9013 8.70174 51.3028 9.29892C50.7044 9.89611 50.2967 10.6569 50.1313 11.4851C50.1017 11.6304 50.0227 11.7611 49.9077 11.8549C49.7926 11.9487 49.6486 12 49.5 12C49.3514 12 49.2074 11.9487 49.0923 11.8549C48.9773 11.7611 48.8983 11.6304 48.8687 11.4851C48.7033 10.6569 48.2956 9.89611 47.6972 9.29892C47.0987 8.70174 46.3362 8.29494 45.5062 8.12991C45.3626 8.09801 45.2342 8.01821 45.1422 7.9037C45.0501 7.78918 45 7.64678 45 7.5C45 7.35322 45.0501 7.21082 45.1422 7.0963C45.2342 6.98178 45.3626 6.90199 45.5062 6.87009Z"
          fill="url(#paint0_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-2"
          style={{ transformOrigin: "35.5 120" }}
          d="M30.1749 119.16C31.2817 118.94 32.2982 118.398 33.0962 117.601C33.8942 116.805 34.4378 115.791 34.6583 114.686C34.6977 114.493 34.8031 114.319 34.9565 114.193C35.1099 114.068 35.3019 114 35.5 114C35.6981 114 35.8901 114.068 36.0435 114.193C36.1969 114.319 36.3023 114.493 36.3417 114.686C36.5622 115.791 37.1058 116.805 37.9038 117.601C38.7018 118.398 39.7183 118.94 40.8251 119.16C41.0165 119.203 41.1877 119.309 41.3104 119.462C41.4331 119.614 41.5 119.804 41.5 120C41.5 120.196 41.4331 120.386 41.3104 120.538C41.1877 120.691 41.0165 120.797 40.8251 120.84C39.7183 121.06 38.7018 121.602 37.9038 122.399C37.1058 123.195 36.5622 124.209 36.3417 125.314C36.3023 125.507 36.1969 125.681 36.0435 125.807C35.8901 125.932 35.6981 126 35.5 126C35.3019 126 35.1099 125.932 34.9565 125.807C34.8031 125.681 34.6977 125.507 34.6583 125.314C34.4378 124.209 33.8942 123.195 33.0962 122.399C32.2982 121.602 31.2817 121.06 30.1749 120.84C29.9835 120.797 29.8123 120.691 29.6896 120.538C29.5669 120.386 29.5 120.196 29.5 120C29.5 119.804 29.5669 119.614 29.6896 119.462C29.8123 119.309 29.9835 119.203 30.1749 119.16Z"
          fill="url(#paint1_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-3"
          style={{ transformOrigin: "10.5 95" }}
          d="M6.94993 94.4401C7.68777 94.2934 8.36549 93.9318 8.89748 93.401C9.42946 92.8701 9.79185 92.1939 9.93886 91.4576C9.96514 91.3285 10.0354 91.2124 10.1376 91.129C10.2399 91.0456 10.3679 91 10.5 91C10.6321 91 10.7601 91.0456 10.8624 91.129C10.9646 91.2124 11.0349 91.3285 11.0611 91.4576C11.2082 92.1939 11.5705 92.8701 12.1025 93.401C12.6345 93.9318 13.3122 94.2934 14.0501 94.4401C14.1777 94.4684 14.2918 94.5394 14.3736 94.6412C14.4554 94.7429 14.5 94.8695 14.5 95C14.5 95.1305 14.4554 95.2571 14.3736 95.3588C14.2918 95.4606 14.1777 95.5316 14.0501 95.5599C13.3122 95.7066 12.6345 96.0682 12.1025 96.599C11.5705 97.1299 11.2082 97.8061 11.0611 98.5424C11.0349 98.6715 10.9646 98.7876 10.8624 98.871C10.7601 98.9544 10.6321 99 10.5 99C10.3679 99 10.2399 98.9544 10.1376 98.871C10.0354 98.7876 9.96514 98.6715 9.93886 98.5424C9.79185 97.8061 9.42946 97.1299 8.89748 96.599C8.36549 96.0682 7.68777 95.7066 6.94993 95.5599C6.8223 95.5316 6.70817 95.4606 6.62637 95.3588C6.54458 95.2571 6.5 95.1305 6.5 95C6.5 94.8695 6.54458 94.7429 6.62637 94.6412C6.70817 94.5394 6.8223 94.4684 6.94993 94.4401Z"
          fill="url(#paint2_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-4"
          style={{ transformOrigin: "124 113.5" }}
          d="M115.569 112.17C117.321 111.822 118.931 110.963 120.194 109.702C121.457 108.442 122.318 106.835 122.667 105.087C122.73 104.78 122.897 104.504 123.139 104.306C123.382 104.108 123.686 104 124 104C124.314 104 124.618 104.108 124.861 104.306C125.103 104.504 125.27 104.78 125.333 105.087C125.682 106.835 126.543 108.442 127.806 109.702C129.069 110.963 130.679 111.822 132.431 112.17C132.735 112.238 133.006 112.406 133.2 112.648C133.394 112.889 133.5 113.19 133.5 113.5C133.5 113.81 133.394 114.11 133.2 114.352C133.006 114.594 132.735 114.762 132.431 114.83C130.679 115.178 129.069 116.037 127.806 117.298C126.543 118.558 125.682 120.165 125.333 121.913C125.27 122.22 125.103 122.496 124.861 122.694C124.618 122.892 124.314 123 124 123C123.686 123 123.382 122.892 123.139 122.694C122.897 122.496 122.73 122.22 122.667 121.913C122.318 120.165 121.457 118.558 120.194 117.298C118.931 116.037 117.321 115.178 115.569 114.83C115.265 114.762 114.994 114.594 114.8 114.352C114.606 114.11 114.5 113.81 114.5 113.5C114.5 113.19 114.606 112.889 114.8 112.648C114.994 112.406 115.265 112.238 115.569 112.17Z"
          fill="url(#paint3_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-5"
          style={{ transformOrigin: "131.5 65.5" }}
          d="M127.506 64.8701C128.336 64.7051 129.099 64.2983 129.697 63.7011C130.296 63.1039 130.703 62.3431 130.869 61.5149C130.898 61.3696 130.977 61.2389 131.092 61.1451C131.207 61.0513 131.351 61 131.5 61C131.649 61 131.793 61.0513 131.908 61.1451C132.023 61.2389 132.102 61.3696 132.131 61.5149C132.297 62.3431 132.704 63.1039 133.303 63.7011C133.901 64.2983 134.664 64.7051 135.494 64.8701C135.637 64.902 135.766 64.9818 135.858 65.0963C135.95 65.2108 136 65.3532 136 65.5C136 65.6468 135.95 65.7892 135.858 65.9037C135.766 66.0182 135.637 66.098 135.494 66.1299C134.664 66.2949 133.901 66.7017 133.303 67.2989C132.704 67.8961 132.297 68.6569 132.131 69.4851C132.102 69.6304 132.023 69.7611 131.908 69.8549C131.793 69.9487 131.649 70 131.5 70C131.351 70 131.207 69.9487 131.092 69.8549C130.977 69.7611 130.898 69.6304 130.869 69.4851C130.703 68.6569 130.296 67.8961 129.697 67.2989C129.099 66.7017 128.336 66.2949 127.506 66.1299C127.363 66.098 127.234 66.0182 127.142 65.9037C127.05 65.7892 127 65.6468 127 65.5C127 65.3532 127.05 65.2108 127.142 65.0963C127.234 64.9818 127.363 64.902 127.506 64.8701Z"
          fill="url(#paint4_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-6"
          style={{ transformOrigin: "16.5 50.5" }}
          d="M9.84363 49.4502C11.2271 49.1751 12.4978 48.4971 13.4953 47.5018C14.4927 46.5065 15.1722 45.2385 15.4479 43.8581C15.4971 43.6159 15.6288 43.3982 15.8206 43.2418C16.0123 43.0854 16.2523 43 16.5 43C16.7476 43 16.9877 43.0854 17.1794 43.2418C17.3712 43.3982 17.5029 43.6159 17.5521 43.8581C17.8278 45.2385 18.5073 46.5065 19.5047 47.5018C20.5022 48.4971 21.7729 49.1751 23.1564 49.4502C23.3957 49.5033 23.6097 49.6363 23.763 49.8272C23.9164 50.018 24 50.2554 24 50.5C24 50.7446 23.9164 50.982 23.763 51.1728C23.6097 51.3637 23.3957 51.4967 23.1564 51.5498C21.7729 51.8249 20.5022 52.5029 19.5047 53.4982C18.5073 54.4935 17.8278 55.7615 17.5521 57.1419C17.5029 57.3841 17.3712 57.6018 17.1794 57.7582C16.9877 57.9146 16.7476 58 16.5 58C16.2523 58 16.0123 57.9146 15.8206 57.7582C15.6288 57.6018 15.4971 57.3841 15.4479 57.1419C15.1722 55.7615 14.4927 54.4935 13.4953 53.4982C12.4978 52.5029 11.2271 51.8249 9.84363 51.5498C9.60431 51.4967 9.39032 51.3637 9.23695 51.1728C9.08358 50.982 9 50.7446 9 50.5C9 50.2554 9.08358 50.018 9.23695 49.8272C9.39032 49.6363 9.60431 49.5033 9.84363 49.4502Z"
          fill="url(#paint5_linear_18211_99469)"
        />
        <path
          className="pulsing-star star-7"
          style={{ transformOrigin: "109 24" }}
          d="M101.012 22.7402C102.672 22.4101 104.197 21.5965 105.394 20.4022C106.591 19.2078 107.407 17.6862 107.737 16.0297C107.797 15.7391 107.955 15.4778 108.185 15.2902C108.415 15.1025 108.703 15 109 15C109.297 15 109.585 15.1025 109.815 15.2902C110.045 15.4778 110.203 15.7391 110.263 16.0297C110.593 17.6862 111.409 19.2078 112.606 20.4022C113.803 21.5965 115.328 22.4101 116.988 22.7402C117.275 22.804 117.532 22.9636 117.716 23.1926C117.9 23.4216 118 23.7064 118 24C118 24.2936 117.9 24.5784 117.716 24.8074C117.532 25.0364 117.275 25.196 116.988 25.2598C115.328 25.5899 113.803 26.4035 112.606 27.5978C111.409 28.7922 110.593 30.3138 110.263 31.9703C110.203 32.2609 110.045 32.5222 109.815 32.7098C109.585 32.8975 109.297 33 109 33C108.703 33 108.415 32.8975 108.185 32.7098C107.955 32.5222 107.797 32.2609 107.737 31.9703C107.407 30.3138 106.591 28.7922 105.394 27.5978C104.197 26.4035 102.672 25.5899 101.012 25.2598C100.725 25.196 100.468 25.0364 100.284 24.8074C100.1 24.5784 100 24.2936 100 24C100 24.2936 100.1 23.4216 100.284 23.1926C100.468 22.9636 100.725 22.804 101.012 22.7402Z"
          fill="url(#paint6_linear_18211_99469)"
        />
        <defs>
          <filter
            id="filter0_f_18211_99469"
            x="0.6"
            y="0.6"
            width="147.8"
            height="147.8"
            filterUnits="userSpaceOnUse"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="11.2"
              result="effect1_foregroundBlur_18211_99469"
            />
          </filter>
          <pattern
            id="pattern0_18211_99469"
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use
              xlinkHref="#image0_18211_99469"
              transform="translate(0 -0.0977564) scale(0.00160256)"
            />
          </pattern>
          <linearGradient
            id="paint0_linear_18211_99469"
            x1="49.5"
            y1="3"
            x2="49.5"
            y2="12"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_18211_99469"
            x1="35.5"
            y1="114"
            x2="35.5"
            y2="126"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_18211_99469"
            x1="10.5"
            y1="91"
            x2="10.5"
            y2="99"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_18211_99469"
            x1="124"
            y1="104"
            x2="124"
            y2="123"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint4_linear_18211_99469"
            x1="131.5"
            y1="61"
            x2="131.5"
            y2="70"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint5_linear_18211_99469"
            x1="16.5"
            y1="43"
            x2="16.5"
            y2="58"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <linearGradient
            id="paint6_linear_18211_99469"
            x1="109"
            y1="15"
            x2="109"
            y2="33"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FFEEBB" />
            <stop offset="1" stop-color="#E69E33" />
          </linearGradient>
          <clipPath id="clip0_18211_99469">
            <rect
              x="26.5"
              y="27"
              width="96"
              height="95"
              rx="47.5"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      <style jsx>{`
  .pulsing-star {
  animation: pulse 3s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: center;
}

.star-1 {
  animation-delay: 0s;
}

.star-2 {
  animation-delay: 0.5s;
}

.star-3 {
  animation-delay: 1s;
}

.star-4 {
  animation-delay: 0.6s;
}

.star-5 {
  animation-delay: 0.8s;
}

.star-6 {
  animation-delay: 1s;
}

.star-7 {
  animation-delay: 1.2s;
}

@keyframes pulse {
  0% {
    transform: scale(0);
    opacity: 1;
    filter: drop-shadow(0 0 2px rgba(255, 238, 187, 0.8));
}
  50% {
    transform: scale(1.2);
    opacity: 0.5;
    filter: drop-shadow(0 0 8px rgba(255, 238, 187, 1));
}
  100% {
    transform: scale(0);
    opacity: 1;
    filter: drop-shadow(0 0 2px rgba(255, 238, 187, 0.8));
}
}
`}</style>
    </div>
  );
};

export default AvatarFrame;
